/**
 * useMinistryAdmin Hooks
 *
 * URQL hooks for admin ministry management (query + mutations).
 */

import { useCallback } from "react";
import { useQuery, useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// QUERY DOCUMENTS
// =============================================================================

const MinistryDocument = `
  query Ministry($id: ID!) {
    ministry(id: $id) {
      id
      code
      name
      description
      imageUrl
      isActive
      sortOrder
      members {
        id
        account { id firstName lastName username }
        role
        isActive
      }
    }
  }
`;

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const CreateMinistryDocument = `
  mutation CreateMinistry($input: MinistryInput!) {
    createMinistry(input: $input) { id }
  }
`;

const UpdateMinistryDocument = `
  mutation UpdateMinistry($id: ID!, $input: MinistryInput!) {
    updateMinistry(id: $id, input: $input) { id }
  }
`;

const DeleteMinistryDocument = `
  mutation DeleteMinistry($id: ID!) {
    deleteMinistry(id: $id)
  }
`;

const AddMemberDocument = `
  mutation AddMinistryMember($ministryId: ID!, $input: AddMinistryMemberInput!) {
    addMinistryMember(ministryId: $ministryId, input: $input) {
      id
      account { id firstName lastName username }
      role
      isActive
    }
  }
`;

const UpdateMemberDocument = `
  mutation UpdateMinistryMember($memberId: ID!, $role: MinistryRole!) {
    updateMinistryMember(memberId: $memberId, role: $role) { id role }
  }
`;

const RemoveMemberDocument = `
  mutation RemoveMinistryMember($memberId: ID!) {
    removeMinistryMember(memberId: $memberId)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface AdminAccountRef {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
}

// Use same MinistryRole as parent types
type MinistryRole = "MEMBER" | "LEADER" | "ADMIN";

interface MinistryMember {
  id: string;
  account: AdminAccountRef;
  role: MinistryRole;
  isActive: boolean;
}

interface Ministry {
  id: string;
  code: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  isActive: boolean;
  sortOrder: number;
  members: MinistryMember[];
}

interface MinistryInput {
  code?: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface AddMemberInput {
  accountId: string;
  role: MinistryRole;
}

// =============================================================================
// QUERY HOOK
// =============================================================================

interface UseMinistryResult {
  ministry: Ministry | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useMinistry(ministryId: string | undefined): UseMinistryResult {
  const [result, reexecuteQuery] = useQuery<{ ministry: Ministry | null }>({
    query: MinistryDocument,
    variables: { id: ministryId },
    pause: !ministryId,
  });

  return {
    ministry: result.data?.ministry ?? null,
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

interface UseCreateMinistryResult {
  createMinistry: (input: MinistryInput) => Promise<string>;
  isLoading: boolean;
}

export function useCreateMinistry(): UseCreateMinistryResult {
  const [result, executeMutation] = useMutation<{ createMinistry: { id: string } }>(
    CreateMinistryDocument
  );

  const createMinistry = useCallback(
    async (input: MinistryInput): Promise<string> => {
      const response = await executeMutation({ input });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка создания служения");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-ministries", action: "createMinistry" },
        });
        throw err;
      }
      
      if (!response.data?.createMinistry?.id) {
        throw new Error("No data returned from createMinistry");
      }
      return response.data.createMinistry.id;
    },
    [executeMutation]
  );

  return { createMinistry, isLoading: result.fetching };
}

interface UseUpdateMinistryResult {
  updateMinistry: (id: string, input: MinistryInput) => Promise<void>;
  isLoading: boolean;
}

export function useUpdateMinistry(): UseUpdateMinistryResult {
  const [result, executeMutation] = useMutation<{ updateMinistry: { id: string } }>(
    UpdateMinistryDocument
  );

  const updateMinistry = useCallback(
    async (id: string, input: MinistryInput): Promise<void> => {
      const response = await executeMutation({ id, input });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка обновления служения");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-ministries", action: "updateMinistry" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { updateMinistry, isLoading: result.fetching };
}

interface UseDeleteMinistryResult {
  deleteMinistry: (id: string) => Promise<void>;
  isLoading: boolean;
}

export function useDeleteMinistry(): UseDeleteMinistryResult {
  const [result, executeMutation] = useMutation<{ deleteMinistry: boolean }>(
    DeleteMinistryDocument
  );

  const deleteMinistry = useCallback(
    async (id: string): Promise<void> => {
      const response = await executeMutation({ id });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка удаления служения");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-ministries", action: "deleteMinistry" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { deleteMinistry, isLoading: result.fetching };
}

interface UseAddMinistryMemberResult {
  addMember: (ministryId: string, input: AddMemberInput) => Promise<MinistryMember>;
  isLoading: boolean;
}

export function useAddMinistryMember(): UseAddMinistryMemberResult {
  const [result, executeMutation] = useMutation<{
    addMinistryMember: MinistryMember;
  }>(AddMemberDocument);

  const addMember = useCallback(
    async (ministryId: string, input: AddMemberInput): Promise<MinistryMember> => {
      const response = await executeMutation({ ministryId, input });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка добавления участника");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-ministries", action: "addMember" },
        });
        throw err;
      }
      
      if (!response.data?.addMinistryMember) {
        throw new Error("No data returned from addMinistryMember");
      }
      return response.data.addMinistryMember;
    },
    [executeMutation]
  );

  return { addMember, isLoading: result.fetching };
}

interface UseUpdateMinistryMemberResult {
  updateMemberRole: (memberId: string, role: MinistryRole) => Promise<void>;
  isLoading: boolean;
}

export function useUpdateMinistryMember(): UseUpdateMinistryMemberResult {
  const [result, executeMutation] = useMutation<{
    updateMinistryMember: { id: string; role: MinistryRole };
  }>(UpdateMemberDocument);

  const updateMemberRole = useCallback(
    async (memberId: string, role: MinistryRole): Promise<void> => {
      const response = await executeMutation({ memberId, role });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка обновления роли");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-ministries", action: "updateMemberRole" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { updateMemberRole, isLoading: result.fetching };
}

interface UseRemoveMinistryMemberResult {
  removeMember: (memberId: string) => Promise<void>;
  isLoading: boolean;
}

export function useRemoveMinistryMember(): UseRemoveMinistryMemberResult {
  const [result, executeMutation] = useMutation<{ removeMinistryMember: boolean }>(
    RemoveMemberDocument
  );

  const removeMember = useCallback(
    async (memberId: string): Promise<void> => {
      const response = await executeMutation({ memberId });
      if (response.error) {
        const message = extractGraphQLErrorMessage(response.error, "Ошибка удаления участника");
        const err = new Error(message);
        errorTracking.captureError(err, {
          tags: { feature: "admin-ministries", action: "removeMember" },
        });
        throw err;
      }
    },
    [executeMutation]
  );

  return { removeMember, isLoading: result.fetching };
}
